/* ==========================================================================
   <fe-honeycomb> — the animated honeycomb that carries the hero from light to
   brand teal as the page scrolls.

   The engine is the Living Texture Studio developer export ("FE Homepage ·
   Hero Background", exported 2026-09-16) with both approved endpoint looks
   baked in: "Final Texture - Light" over white and "Final Texture - Dark" over
   brand teal. Every numeric setting blends between the two with the scroll
   progress, the categorical ones switch at the midpoint, and the copy's colours
   travel with the ground: the element writes one CSS custom property per text
   role onto its host, guarded so an in-between colour is never left unreadable
   over an in-between ground. The drawing code is the export's; the wiring —
   the custom element, the palette contract, the idle handling — is the theme's.

   The export's contract is kept whole. By default the fade reads the window
   scroll from the top of the page over data-start → data-end viewport heights
   (0 → 1.85, the export's range, so the site scrolls exactly like preview.html);
   the canvas is #fe-honeycomb-canvas; the host receives --fe-foreground
   (#323232 → #FFFFFF) and --fe-accent (#025D7F → #02D59E) precisely as the
   export's applyForegroundColor wrote them; and window.FE_HONEYCOMB offers the
   README's useWindowScroll(false) / setScrollProgress(p) / redraw() handle for
   a site controller to take over. On top of that the theme adds its own roles.

   Attributes
     data-start      viewport heights of scroll before the fade begins (0)
     data-end        viewport heights of scroll where it is complete (1.85)
     data-driven     present = the section feeds the progress itself through
                     el.setProgress(0..1) instead of the window scroll
     data-hold       0..1, a blend held there for good: 0 is the light ground,
                     1 the teal one. For a section that wants the texture at
                     one colour rather than a fade between two. Beats both of
                     the above.
     data-motion     auto | lite | static. Static paints one still frame and
                     repaints it as the progress moves, which is also what a
                     reduced-motion visitor gets whatever this says. Lite drops
                     the cursor glow and the click ring.
     data-palette    JSON, {role: [lightHex, darkHex, minContrast]}. Each role
                     lands on the host as --fe-{role}-color, guarded like the
                     export's two.
     data-paused     present = never schedule a frame; tests step el.tick(ms).

   The host is the closest [data-fe-honeycomb-host], else the parent. The
   element fires "fe-honeycomb:progress" (bubbling) whenever its progress moves,
   with the eased value, the ground colour, the shade and every colour in detail.
   ========================================================================== */

(() => {
  'use strict';

  /* The snippet emits this tag once per ground on the page, and from here on
     that is most sections rather than only the hero. The browser downloads
     the file once and runs it once per tag; everything below is definitions,
     and only the first run's element registration is ever used. Leave after
     it. */
  if (customElements.get('fe-honeycomb')) return;

  const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');

  /* --------------------------------------------------------- the two looks
     The export's endpoint snapshots, verbatim. Anything not listed in DARK is
     the same at both ends. */

  const LIGHT = Object.freeze({
    theme: 'light',
    scale: 37,
    waves: false,
    waveAmt: 0.5,
    sweep: true,
    sweepBrightness: 2.55,
    sweepSpeed: 0.9,
    sweepWidth: 2.5,
    sweepSoftness: 5.3,
    sweepAngle: 45,
    pulses: true,
    pulseRate: 0.3,
    pulseSpeed: 0.4,
    pulseSize: 0.7,
    pulseTrail: 13,
    pulseGlow: 0.2,
    glow: true,
    glowRadius: 190,
    glowStr: 0.2,
    glowContrast: 0.4,
    glowOrganic: 0.35,
    glowDrag: 1.25,
    clickRing: true,
    ringRadius: 225,
    ringDuration: 1.2,
    ringOpacity: 0.04,
    ringWidth: 2.4,
    warp: false,
    warpStr: 0,
    glints: false,
    lineStr: 0.35,
    lineContrast: 1,
    rainbow: 0.05,
    rainbowSpeed: 1.9,
    rainbowSpread: 1.8,
    fill: true,
    fillStyle: 'dimensional',
    fillColor: 'mixed',
    fillCustom: '#02D59E',
    fillOpacity: 0.03,
    fillDensity: 0.74,
    fillDepth: 1,
    fillSpeed: 0.84,
    fillMotion: true,
    fillCursor: true,
  });

  const DARK = Object.freeze({
    ...LIGHT,
    theme: 'teal',
    waveAmt: 0,
    sweepBrightness: 2.25,
    sweepWidth: 2,
    glowRadius: 155,
    glowStr: 0.15,
    glowContrast: 2.3,
    glowOrganic: 1.25,
    glowDrag: 1.05,
    lineStr: 0.4,
    fillColor: 'custom',
    fillCustom: '#020303',
    fillOpacity: 0.04,
    fillDensity: 0.49,
    fillDepth: 0.51,
    fillSpeed: 1.8,
  });

  const KEYS = Object.keys(LIGHT);

  /* the export's scroll scene: easing, range in viewport heights, and the two
     copy colours it hands the page */
  const EASING = 'smoothstep';
  const RANGE = Object.freeze({ start: 0, end: 1.85 });
  const TEXT = Object.freeze({
    textLight: '#323232',
    textDark: '#FFFFFF',
    accentLight: '#025D7F',
    accentDark: '#02D59E',
  });
  const LABELS = Object.freeze({ light: 'Final Texture - Light', dark: 'Final Texture - Dark' });

  /* All rgb triplets trace to documented brand hexes: teal #025D7F, mint
     #02D59E, sky #55D1FF, navy #062B3C, soft glow mint #54C6A0. */
  const THEMES = {
    light: {
      bgTop: '#FFFFFF',
      bgBot: '#F4F4F4',
      line: [2, 213, 158],
      lineA: 0.13,
      glowLine: [2, 213, 158],
      glowCore: [2, 93, 127],
      glowFill: [2, 213, 158],
      gain: 0.55,
      pulse: [
        [2, 93, 127],
        [2, 213, 158],
        [85, 209, 255],
      ],
      blend: 'source-over',
      dark: false,
    },
    teal: {
      bgTop: '#025D7F',
      bgBot: '#025D7F',
      line: [255, 255, 255],
      lineA: 0.13,
      glowLine: [85, 209, 255],
      glowCore: [255, 255, 255],
      glowFill: [2, 213, 158],
      gain: 0.9,
      pulse: [
        [85, 209, 255],
        [2, 213, 158],
      ],
      blend: 'screen',
      dark: true,
    },
    navy: {
      bgTop: '#062B3C',
      bgBot: '#062B3C',
      line: [2, 213, 158],
      lineA: 0.15,
      glowLine: [2, 213, 158],
      glowCore: [255, 255, 255],
      glowFill: [85, 209, 255],
      gain: 0.9,
      pulse: [
        [2, 213, 158],
        [85, 209, 255],
      ],
      blend: 'screen',
      dark: true,
      corners: [
        [0.16, 0.14, [84, 198, 160], 0.2],
        [0.86, 0.08, [85, 209, 255], 0.1],
      ],
    },
  };

  /* ---------------------------------------------------------------- colour */

  const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a.toFixed(3)})`;
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

  /* Brightness/contrast shaping for the cursor-glow field: contrast > 1 punches
     up the hot core and lets the tail fall away faster, < 1 flattens it into a
     broader wash. Keeps the 0..~1.15 range sampleField() produces. */
  function shapeGlow(v, c) {
    if (v <= 0) return 0;
    let n = v / 1.15;
    if (n > 1) n = 1;
    return Math.pow(n, c) * 1.15;
  }

  /* Rainbow tint is blended ON TOP of brand colours (the rainbow setting is
     the mix amount, 0 = pure brand), never replacing them. */
  function hue2rgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  }

  function hsl2rgb(h, s, l) {
    h = (((h % 360) + 360) % 360) / 360;
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    return [
      Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
      Math.round(hue2rgb(p, q, h) * 255),
      Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
    ];
  }

  function mixColor(a, b, t) {
    if (t <= 0) return a;
    if (t >= 1) return b;
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
  }

  function scrollEase(v, kind) {
    v = clamp01(v);
    if (kind === 'linear') return v;
    if (kind === 'easeInOut') return 0.5 - 0.5 * Math.cos(Math.PI * v);
    return v * v * (3 - 2 * v);
  }

  function hexRGB(value) {
    const m = /^#([0-9a-f]{6})$/i.exec(String(value || '').trim());
    return m
      ? [parseInt(m[1].slice(0, 2), 16), parseInt(m[1].slice(2, 4), 16), parseInt(m[1].slice(4, 6), 16)]
      : [0, 0, 0];
  }

  const rgbCSS = (value) => `rgb(${value.map((n) => Math.round(n)).join(',')})`;

  function relativeLuminance(rgb) {
    const c = rgb.map((n) => {
      n = clamp01(n / 255);
      return n <= 0.04045 ? n / 12.92 : Math.pow((n + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  }

  function contrastRatio(a, b) {
    const x = relativeLuminance(a);
    const y = relativeLuminance(b);
    return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
  }

  function themeBaseRGB(name) {
    const th = THEMES[name] || THEMES.navy;
    return mixColor(hexRGB(th.bgTop), hexRGB(th.bgBot), 0.5);
  }

  /* The interpolated colour if it still reads over the blended ground; else
     whichever of the two ends, FE navy or white reads best there. */
  function contrastSafe(from, to, p, background, minimum) {
    const preferred = mixColor(hexRGB(from), hexRGB(to), p);
    let best = preferred;
    let bestRatio = contrastRatio(preferred, background);
    if (bestRatio >= minimum) return preferred;
    for (const value of [from, to, '#062B3C', '#FFFFFF']) {
      const candidate = hexRGB(value);
      const ratio = contrastRatio(candidate, background);
      if (ratio > bestRatio) {
        best = candidate;
        bestRatio = ratio;
      }
    }
    return best;
  }

  function blendCorners(a, b, p) {
    a = a || [];
    b = b || [];
    const count = Math.max(a.length, b.length);
    const out = [];
    for (let i = 0; i < count; i++) {
      const ac = a[i];
      const bc = b[i];
      if (ac && bc) out.push([ac[0] + (bc[0] - ac[0]) * p, ac[1] + (bc[1] - ac[1]) * p, mixColor(ac[2], bc[2], p), ac[3] + (bc[3] - ac[3]) * p]);
      else if (ac) out.push([ac[0], ac[1], ac[2], ac[3] * (1 - p)]);
      else if (bc) out.push([bc[0], bc[1], bc[2], bc[3] * p]);
    }
    return out;
  }

  function resolveTheme(settings) {
    if (!settings.__themeBlend) return THEMES[settings.theme] || THEMES.navy;
    const blend = settings.__themeBlend;
    const p = blend.mix;
    const a = THEMES[blend.from] || THEMES.navy;
    const b = THEMES[blend.to] || THEMES.navy;
    const pulse = [];
    const count = Math.max(a.pulse.length, b.pulse.length);
    for (let i = 0; i < count; i++) pulse.push(mixColor(a.pulse[i % a.pulse.length], b.pulse[i % b.pulse.length], p));
    return {
      bgTop: rgbCSS(mixColor(hexRGB(a.bgTop), hexRGB(b.bgTop), p)),
      bgBot: rgbCSS(mixColor(hexRGB(a.bgBot), hexRGB(b.bgBot), p)),
      line: mixColor(a.line, b.line, p),
      lineA: a.lineA + (b.lineA - a.lineA) * p,
      glowLine: mixColor(a.glowLine, b.glowLine, p),
      glowCore: mixColor(a.glowCore, b.glowCore, p),
      glowFill: mixColor(a.glowFill, b.glowFill, p),
      gain: a.gain + (b.gain - a.gain) * p,
      pulse,
      blend: p < 0.5 ? a.blend : b.blend,
      dark: p < 0.5 ? a.dark : b.dark,
      corners: blendCorners(a.corners, b.corners, p),
    };
  }

  /* --------------------------------------------------------------- palette */

  function parsePalette(json) {
    const out = {};
    if (!json) return out;
    try {
      const raw = JSON.parse(json);
      for (const name in raw) {
        const entry = raw[name];
        if (!Array.isArray(entry) || entry.length < 2) continue;
        if (!/^[a-z][a-z0-9-]*$/i.test(name)) continue;
        out[name] = [String(entry[0]), String(entry[1]), Number(entry[2]) || 4.5];
      }
    } catch (e) {
      /* a malformed palette leaves the stylesheet's colours in charge */
    }
    return out;
  }

  /* ---------------------------------------------------------------- engine */

  function createEngine(el, canvas, host, opts) {
    const ctx = canvas.getContext('2d');
    const still = opts.still;

    let mix = 0; /* scroll progress, raw 0..1 */
    let cfg = frameSettings(); /* the settings the current frame draws with */
    let W = 0;
    let H = 0;
    let DPR = 1;

    /* Every setting blended for the current progress. Numbers travel; the
       pattern scale, the booleans and the categorical modes switch at the
       midpoint, exactly as the studio previewed it. */
    function frameSettings() {
      const p = scrollEase(mix, EASING);
      const out = {};
      for (const key of KEYS) {
        const av = LIGHT[key];
        const bv = DARK[key];
        if (key === 'scale') out[key] = p < 0.5 ? av : bv;
        else if (typeof av === 'number' && typeof bv === 'number') out[key] = av + (bv - av) * p;
        else out[key] = p < 0.5 ? av : bv;
      }
      out.theme = p < 0.5 ? LIGHT.theme : DARK.theme;
      out.__themeBlend = { from: LIGHT.theme, to: DARK.theme, mix: p };
      if (opts.lite) {
        out.glow = false;
        out.clickRing = false;
        out.fillCursor = false;
      }
      if (still) {
        /* the export's reduced-motion frame: these five off, everything else as is */
        out.waves = false;
        out.sweep = false;
        out.pulses = false;
        out.glow = false;
        out.glints = false;
      }
      return out;
    }

    function colorState(value) {
      const p = scrollEase(value, EASING);
      return { p, background: mixColor(themeBaseRGB(LIGHT.theme), themeBaseRGB(DARK.theme), p) };
    }

    /* The ground colour at a progress, the export's two copy colours over it
       (foreground guarded to 4.5, accent to 3, exactly as its
       applyForegroundColor did), the theme's extra roles guarded the same way,
       and which of the two treatments the ground now asks of anything that has
       only two — a label pill, a mark: "dark" from the moment white reads
       better over the ground than FE navy does, which is the same moment the
       guard starts sending the copy light. */
    function colorsAt(value) {
      const state = colorState(clamp01(Number(value) || 0));
      const foreground = rgbCSS(contrastSafe(TEXT.textLight, TEXT.textDark, state.p, state.background, 4.5));
      const accent = rgbCSS(contrastSafe(TEXT.accentLight, TEXT.accentDark, state.p, state.background, 3));
      const roles = {};
      for (const name in opts.palette) {
        const [from, to, minimum] = opts.palette[name];
        roles[name] = rgbCSS(contrastSafe(from, to, state.p, state.background, minimum));
      }
      const shade =
        contrastRatio([255, 255, 255], state.background) >= contrastRatio(hexRGB('#062B3C'), state.background)
          ? 'dark'
          : 'light';
      return { progress: value, eased: state.p, background: rgbCSS(state.background), shade, foreground, accent, roles };
    }

    function applyPalette() {
      const colors = colorsAt(mix);
      host.style.setProperty('--fe-foreground', colors.foreground);
      host.style.setProperty('--fe-accent', colors.accent);
      for (const name in colors.roles) host.style.setProperty(`--fe-${name}-color`, colors.roles[name]);
      /* the element behind the canvas takes the same ground, so a subpixel of
         it showing at an edge is never a seam */
      el.style.backgroundColor = colors.background;
      el.dispatchEvent(new CustomEvent('fe-honeycomb:progress', { bubbles: true, detail: colors }));
    }

    /* ------------------------------------------------------------- canvas */

    function sizeCanvas() {
      DPR = Math.min(2, devicePixelRatio || 1);
      const bounds = el.getBoundingClientRect();
      W = Math.max(1, Math.round(bounds.width));
      H = Math.max(1, Math.round(bounds.height));
      canvas.width = Math.round(W * DPR);
      canvas.height = Math.round(H * DPR);
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }

    /* ----------------------------------------------------------- geometry
       Rhombille / cube tiling: pointy-top hexagons, corners at screen angles
       30..330 step 60 (y grows downward). Cube look = hex outline + Y-spokes
       from the centre to the corners at 90°, 210°, 330°. Every edge has rest
       length = scale. */

    let verts = [];
    let edges = [];
    let faces = [];
    let adj = [];
    let geometryScale = 0;
    let px;
    let py;
    const COS = [];
    const SIN = [];
    for (let k = 0; k < 6; k++) {
      const a = ((30 + 60 * k) * Math.PI) / 180;
      COS.push(Math.cos(a));
      SIN.push(Math.sin(a));
    }

    function buildGeometry() {
      const s = cfg.scale;
      const dx = Math.sqrt(3) * s;
      const dy = 1.5 * s;
      const m = 2.4 * s;
      geometryScale = s;
      verts = [];
      edges = [];
      faces = [];
      const vmap = Object.create(null);
      const emap = Object.create(null);
      const vid = (x, y) => {
        const key = Math.round(x * 4) + '_' + Math.round(y * 4);
        let i = vmap[key];
        if (i === undefined) {
          i = verts.length;
          verts.push({ rx: x, ry: y, wx: 0, wy: 0, wvx: 0, wvy: 0 });
          vmap[key] = i;
        }
        return i;
      };
      const addEdge = (a, b, kind) => {
        const key = a < b ? a + '_' + b : b + '_' + a;
        if (emap[key]) return;
        emap[key] = 1;
        edges.push({ a, b, kind, mrx: (verts[a].rx + verts[b].rx) / 2, mry: (verts[a].ry + verts[b].ry) / 2 });
      };
      const cols = Math.ceil((W + 2 * m) / dx) + 1;
      const rows = Math.ceil((H + 2 * m) / dy) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const cx = -m + dx * (c + (r % 2 ? 0.5 : 0));
          const cy = -m + dy * r;
          const ci = vid(cx, cy);
          const K = [];
          for (let k = 0; k < 6; k++) K.push(vid(cx + s * COS[k], cy + s * SIN[k]));
          for (let k = 0; k < 6; k++) addEdge(K[k], K[(k + 1) % 6], 0);
          addEdge(ci, K[1], 1);
          addEdge(ci, K[3], 1);
          addEdge(ci, K[5], 1);
          faces.push([ci, K[1], K[2], K[3]], [ci, K[3], K[4], K[5]], [ci, K[5], K[0], K[1]]);
        }
      }
      adj = new Array(verts.length);
      for (let i = 0; i < verts.length; i++) adj[i] = [];
      for (let e = 0; e < edges.length; e++) {
        adj[edges[e].a].push(e);
        adj[edges[e].b].push(e);
      }
      px = new Float32Array(verts.length);
      py = new Float32Array(verts.length);
      pulses.length = 0;
      blips.length = 0;
      glints.length = 0;
    }

    /* ------------------------------------------- input: cursor, trail, bursts */

    const cursor = { x: -9999, y: -9999, active: false };
    const trail = []; /* {x,y,t,dx,dy} */
    const bursts = []; /* {x,y,t,big} */
    let demoDrive = true; /* a ghost cursor until the visitor moves the mouse */

    function pushTrail(x, y, now) {
      const last = trail[trail.length - 1];
      let dx = 0;
      let dy = 0;
      if (last) {
        dx = x - last.x;
        dy = y - last.y;
        if (dx * dx + dy * dy < 100) {
          /* coalesce tiny movements into the newest sample, so a still pointer
             lets the glow decay instead of restarting it */
          last.x = x;
          last.y = y;
          last.t = now;
          last.dx = dx;
          last.dy = dy;
          return;
        }
      }
      trail.push({ x, y, t: now, dx, dy });
      if (trail.length > 90) trail.shift();
    }

    function placeCursor(ev) {
      const rect = canvas.getBoundingClientRect();
      cursor.x = ev.clientX - rect.left;
      cursor.y = ev.clientY - rect.top;
    }

    const onPointerMove = (ev) => {
      demoDrive = false;
      placeCursor(ev);
      cursor.active = cursor.x >= 0 && cursor.y >= 0 && cursor.x <= W && cursor.y <= H;
      if (cfg.glow) pushTrail(cursor.x, cursor.y, tNow);
    };

    const onPointerDown = (ev) => {
      if (!(ev.target instanceof Element)) return;
      if (!ev.target.closest('.fe-honeycomb-hero') && !host.contains(ev.target)) return;
      demoDrive = false;
      placeCursor(ev);
      cursor.active = true;
      bursts.push({ x: cursor.x, y: cursor.y, t: tNow, big: false });
      spawnPulseStorm(cursor.x, cursor.y, 4);
    };

    const onPointerLeave = () => {
      cursor.active = false;
    };

    /* a slow lissajous wander, so the effect shows itself before anyone hovers */
    function ghost(t) {
      return {
        x: W * (0.5 + 0.34 * Math.sin(t * 0.21) + 0.08 * Math.sin(t * 0.53 + 1.2)),
        y: H * (0.44 + 0.27 * Math.sin(t * 0.16 + 1.7) + 0.07 * Math.cos(t * 0.44)),
      };
    }

    /* ------------------------------------------------ glow field (coarse grid) */

    const CELL = 28;
    let gw = 0;
    let gh = 0;
    let fieldGrid = null;

    function sizeGrid() {
      gw = Math.ceil(W / CELL) + 2;
      gh = Math.ceil(H / CELL) + 2;
      fieldGrid = new Float32Array(gw * gh);
    }

    /* Anisotropic splat: (ex,ey) is the unit direction of travel; rAlong/rPerp
       stretch a blob along it and pinch across it — an oil-drop smear rather
       than a disc. Circular case: rAlong === rPerp, ex = 1, ey = 0. */
    function splat(x, y, wgt, rAlong, rPerp, ex, ey) {
      const invA = 1 / (2 * rAlong * rAlong);
      const invP = 1 / (2 * rPerp * rPerp);
      const span = 2.4 * Math.max(rAlong, rPerp);
      const x0 = Math.max(0, Math.floor((x - span) / CELL));
      const x1 = Math.min(gw - 1, Math.ceil((x + span) / CELL));
      const y0 = Math.max(0, Math.floor((y - span) / CELL));
      const y1 = Math.min(gh - 1, Math.ceil((y + span) / CELL));
      for (let gy = y0; gy <= y1; gy++) {
        const yy = gy * CELL - y;
        const row = gy * gw;
        for (let gx = x0; gx <= x1; gx++) {
          const xx = gx * CELL - x;
          const along = xx * ex + yy * ey;
          const perp = xx * -ey + yy * ex;
          fieldGrid[row + gx] += wgt * Math.exp(-(along * along * invA + perp * perp * invP));
        }
      }
    }

    function rebuildField(now) {
      fieldGrid.fill(0);
      const drag = Math.max(0.3, cfg.glowDrag);
      const maxAge = 1.7 * drag;
      const decayRate = 2.1 / drag;
      while (trail.length && now - trail[0].t > maxAge) trail.shift();
      const keepBursts = Math.max(1.1, cfg.clickRing ? Math.max(0.05, cfg.ringDuration) : 0);
      for (let bi = bursts.length - 1; bi >= 0; bi--) if (now - bursts[bi].t > keepBursts) bursts.splice(bi, 1);
      if (!cfg.glow) return;
      const r = cfg.glowRadius;
      const organic = cfg.glowOrganic;
      for (let i = 0; i < trail.length; i++) {
        const p = trail[i];
        const wgt = Math.exp(-(now - p.t) * decayRate);
        if (wgt < 0.04) continue;
        let ex = 1;
        let ey = 0;
        let speedNorm = 0;
        if (p.dx || p.dy) {
          const ln = Math.sqrt(p.dx * p.dx + p.dy * p.dy) || 1;
          ex = p.dx / ln;
          ey = p.dy / ln;
          speedNorm = Math.min(1, ln / 50);
        }
        const rAlong = r * (1 + 0.85 * organic * speedNorm);
        const rPerp = Math.max(r * 0.35, r * (1 - 0.3 * organic));
        splat(p.x, p.y, wgt, rAlong, rPerp, ex, ey);
      }
      for (let i = bursts.length - 1; i >= 0; i--) {
        const b = bursts[i];
        const age = now - b.t;
        if (age > 1.1) continue;
        const br = r * (b.big ? 1.6 : 1.15);
        splat(b.x, b.y, (b.big ? 2.6 : 1.2) * Math.exp(-age * 2.4), br, br, 1, 0);
      }
      if (organic > 0) {
        const mAmt = Math.min(0.5, organic * 0.28);
        let idx = 0;
        for (let gy = 0; gy < gh; gy++) {
          for (let gx = 0; gx < gw; gx++, idx++) {
            if (fieldGrid[idx] <= 0.001) continue;
            let wob = 1 + mAmt * Math.sin(gx * 0.85 + now * 1.6) * Math.cos(gy * 0.8 - now * 1.3 + gx * 0.22);
            if (wob < 0.15) wob = 0.15;
            fieldGrid[idx] *= wob;
          }
        }
      }
    }

    function sampleField(x, y) {
      const fx = x / CELL;
      const fy = y / CELL;
      const gx = Math.floor(fx);
      const gy = Math.floor(fy);
      if (gx < 0 || gy < 0 || gx >= gw - 1 || gy >= gh - 1) return 0;
      const tx = fx - gx;
      const ty = fy - gy;
      const i = gy * gw + gx;
      const v =
        fieldGrid[i] * (1 - tx) * (1 - ty) +
        fieldGrid[i + 1] * tx * (1 - ty) +
        fieldGrid[i + gw] * (1 - tx) * ty +
        fieldGrid[i + gw + 1] * tx * ty;
      return v > 1.15 ? 1.15 : v;
    }

    /* --------------------------------------------------------- waves & warp */

    function updateVerts(t, dt) {
      const amp = cfg.waves ? cfg.waveAmt : 0;
      const doWarp = cfg.warp && cfg.warpStr > 0;
      const cx = cursor.x;
      const cy = cursor.y;
      const haveCur = cursor.active || demoDrive;
      const R = 160;
      const inv = 1 / (2 * R * R);
      const S = cfg.warpStr;
      const kSpring = 50;
      const damp = Math.exp(-10 * dt);
      for (let i = 0; i < verts.length; i++) {
        const v = verts[i];
        let wx = 0;
        let wy = 0;
        if (amp > 0) {
          wx = amp * (0.9 * Math.sin(v.ry * 0.011 + t * 0.55) + 0.6 * Math.sin((v.rx + v.ry) * 0.006 - t * 0.32));
          wy = amp * (0.9 * Math.cos(v.rx * 0.009 + t * 0.48) + 0.6 * Math.cos((v.ry - v.rx) * 0.007 + t * 0.27));
        }
        const bx = v.rx + wx;
        const by = v.ry + wy;
        let tx = 0;
        let ty = 0;
        if (doWarp && haveCur) {
          const ddx = bx - cx;
          const ddy = by - cy;
          const d2 = ddx * ddx + ddy * ddy;
          if (d2 < R * R * 9) {
            const f = S * Math.exp(-d2 * inv);
            const d = Math.sqrt(d2) || 1;
            tx = (ddx / d) * f;
            ty = (ddy / d) * f;
          }
        }
        if (doWarp || v.wx !== 0 || v.wy !== 0 || v.wvx !== 0 || v.wvy !== 0) {
          v.wvx = (v.wvx + (tx - v.wx) * kSpring * dt) * damp;
          v.wvy = (v.wvy + (ty - v.wy) * kSpring * dt) * damp;
          v.wx += v.wvx * dt * 8;
          v.wy += v.wvy * dt * 8;
        }
        px[i] = bx + v.wx;
        py[i] = by + v.wy;
      }
    }

    /* ------------------------------------------------- pulses, blips, glints */

    const pulses = [];
    const blips = [];
    const glints = [];
    let spawnCarry = 0;

    function randomVisibleVertex() {
      for (let tries = 0; tries < 24; tries++) {
        const i = (Math.random() * verts.length) | 0;
        const v = verts[i];
        if (v.rx > -10 && v.rx < W + 10 && v.ry > -10 && v.ry < H + 10) return i;
      }
      return (Math.random() * verts.length) | 0;
    }

    function nearestVertex(x, y) {
      let best = 0;
      let bd = 1e18;
      for (let i = 0; i < verts.length; i++) {
        const ddx = verts[i].rx - x;
        const ddy = verts[i].ry - y;
        const d = ddx * ddx + ddy * ddy;
        if (d < bd) {
          bd = d;
          best = i;
        }
      }
      return best;
    }

    function pickEdge(vi, fromVi, dirx, diry) {
      const list = adj[vi];
      if (!list.length) return -1;
      let bestE = -1;
      let bestS = -1e9;
      for (let i = 0; i < list.length; i++) {
        const e = edges[list[i]];
        const other = e.a === vi ? e.b : e.a;
        if (other === fromVi && list.length > 1) continue;
        const ddx = verts[other].rx - verts[vi].rx;
        const ddy = verts[other].ry - verts[vi].ry;
        const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
        let s = dirx === null ? Math.random() : (ddx / len) * dirx + (ddy / len) * diry + (Math.random() - 0.5) * 0.9;
        if (Math.random() < 0.06) s += Math.random() * 1.5;
        if (s > bestS) {
          bestS = s;
          bestE = list[i];
        }
      }
      return bestE;
    }

    function spawnPulse(atV) {
      if (pulses.length > 26) return;
      const vi = atV === undefined ? randomVisibleVertex() : atV;
      const ei = pickEdge(vi, -1, null, null);
      if (ei < 0) return;
      const cols = resolveTheme(cfg).pulse;
      pulses.push({
        from: vi,
        e: ei,
        tpos: 0,
        speed: (110 + Math.random() * 90) * cfg.pulseSpeed,
        born: tNow,
        life: 3.5 + Math.random() * 3,
        hist: [],
        col: cols[(Math.random() * cols.length) | 0],
        hueSeed: Math.random() * 360 * Math.min(2, cfg.rainbowSpread),
      });
    }

    function spawnPulseStorm(x, y, n) {
      if (!cfg.pulses) return;
      const vi = nearestVertex(x, y);
      for (let i = 0; i < n; i++) spawnPulse(vi);
    }

    function updatePulses(dt) {
      if (cfg.pulses) {
        spawnCarry += cfg.pulseRate * dt;
        while (spawnCarry > 1) {
          spawnCarry -= 1;
          spawnPulse();
        }
      }
      const s = cfg.scale;
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        const age = tNow - p.born;
        if (age > p.life + 1) {
          pulses.splice(i, 1);
          continue;
        }
        if (age > p.life) continue; /* dying: the tail fades, the head stops */
        p.tpos += (p.speed * dt) / s;
        while (p.tpos >= 1) {
          p.tpos -= 1;
          const e = edges[p.e];
          const to = e.a === p.from ? e.b : e.a;
          p.hist.push({ a: p.from, b: to, t: tNow });
          if (p.hist.length > cfg.pulseTrail) p.hist.shift();
          blips.push({ v: to, t: tNow });
          if (blips.length > 90) blips.shift();
          const ddx = verts[to].rx - verts[p.from].rx;
          const ddy = verts[to].ry - verts[p.from].ry;
          const len = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
          const ne = pickEdge(to, p.from, ddx / len, ddy / len);
          if (ne < 0) {
            p.born = tNow - p.life;
            break;
          }
          p.from = to;
          p.e = ne;
        }
      }
      for (let i = blips.length - 1; i >= 0; i--) if (tNow - blips[i].t > 0.6) blips.splice(i, 1);
      if (cfg.glints && Math.random() < dt * 0.8 && glints.length < 10) {
        glints.push({ f: (Math.random() * faces.length) | 0, t: tNow, dur: 2.6 + Math.random() * 2 });
      }
      for (let i = glints.length - 1; i >= 0; i--) if (tNow - glints[i].t > glints[i].dur) glints.splice(i, 1);
    }

    /* -------------------------------------------------------------- render */

    const BUCKETS = 7;

    function facePath(f) {
      ctx.beginPath();
      ctx.moveTo(px[f[0]], py[f[0]]);
      ctx.lineTo(px[f[1]], py[f[1]]);
      ctx.lineTo(px[f[2]], py[f[2]]);
      ctx.lineTo(px[f[3]], py[f[3]]);
      ctx.closePath();
    }

    /* Stable, brand-aware cube fills: one logical cube is three consecutive faces. */
    const FILL_COLORS = {
      mint: [2, 213, 158],
      sky: [85, 209, 255],
      teal: [2, 93, 127],
      white: [255, 255, 255],
      yellow: [255, 201, 46],
    };

    function cubeSeed(x, y) {
      const n = Math.sin(x * 0.07173 + y * 0.11939 + 12.9898) * 43758.5453;
      return n - Math.floor(n);
    }

    function cubeFillColor(seed, t) {
      if (cfg.fillColor === 'custom') {
        const hex = cfg.fillCustom || '#02D59E';
        if (/^#[0-9a-f]{6}$/i.test(hex)) return hexRGB(hex);
        return FILL_COLORS.mint;
      }
      if (cfg.fillColor === 'mixed') return seed < 0.38 ? FILL_COLORS.mint : seed < 0.74 ? FILL_COLORS.sky : FILL_COLORS.teal;
      if (cfg.fillColor === 'iridescent') {
        return mixColor(FILL_COLORS.mint, hsl2rgb((seed * 185 + t * 24 * cfg.fillSpeed) % 360, 0.68, 0.59), 0.63);
      }
      return FILL_COLORS[cfg.fillColor] || FILL_COLORS.mint;
    }

    function drawCubeFills(t) {
      if (!cfg.fill || cfg.fillOpacity <= 0 || cfg.fillDensity <= 0) return;
      const depth = clamp01(cfg.fillDepth);
      const density = clamp01(cfg.fillDensity);
      const style = cfg.fillStyle;
      for (let index = 0; index < faces.length; index += 3) {
        const first = faces[index];
        if (!first) continue;
        const center = first[0];
        const x = verts[center].rx;
        const y = verts[center].ry;
        const seed = cubeSeed(x, y);
        if (seed > density) continue;
        const color = cubeFillColor(seed, t);
        const motion = cfg.fillMotion ? 0.78 + 0.22 * Math.sin(t * cfg.fillSpeed * 2.4 + x * 0.006 + y * 0.009 + seed * 9) : 1;
        const cursorBoost = cfg.fillCursor && cfg.glow ? Math.min(0.65, sampleField(px[center], py[center]) * 0.4) : 0;
        const base = Math.min(0.88, cfg.fillOpacity * motion + cursorBoost * cfg.fillOpacity * 1.6);
        for (let side = 0; side < 3; side++) {
          const face = faces[index + side];
          if (!face) continue;
          if (style === 'mosaic' && cubeSeed(x + side * 71.37, y - side * 23.11) > 0.48) continue;
          let shade = style === 'solid' ? 1 : style === 'glass' ? [0.94, 0.55, 0.27][side] : [1, 0.66, 0.38][side];
          if (style === 'mosaic') shade = [1, 0.74, 0.47][side];
          shade = 1 - (1 - shade) * depth;
          const alpha = Math.min(0.88, Math.max(0.008, base * shade));
          facePath(face);
          if (style === 'glass') {
            const gradient = ctx.createLinearGradient(px[face[1]], py[face[1]], px[face[3]], py[face[3]]);
            gradient.addColorStop(0, rgba(color, Math.min(0.88, alpha * 1.28)));
            gradient.addColorStop(1, rgba(color, Math.max(0.012, alpha * 0.19)));
            ctx.fillStyle = gradient;
          } else {
            ctx.fillStyle = rgba(color, alpha);
          }
          ctx.fill();
        }
      }
    }

    function render(t) {
      const th = resolveTheme(cfg);

      /* rainbow tint, blended on top of the brand palette; once per frame */
      const rbAmt = cfg.rainbow;
      const rbBase = rbAmt > 0 ? hsl2rgb((t * 36 * cfg.rainbowSpeed) % 360, 0.72, th.dark ? 0.62 : 0.46) : null;
      const thLine = rbAmt > 0 ? mixColor(th.line, rbBase, rbAmt) : th.line;
      const thGlowLine = rbAmt > 0 ? mixColor(th.glowLine, rbBase, rbAmt * 0.9) : th.glowLine;
      const thGlowCore = rbAmt > 0 ? mixColor(th.glowCore, rbBase, rbAmt * 0.5) : th.glowCore;
      const thGlowFill = rbAmt > 0 ? mixColor(th.glowFill, rbBase, rbAmt * 0.9) : th.glowFill;
      const pulseCoreBase = th.dark ? [255, 255, 255] : th.glowCore;
      const thPulseCore = rbAmt > 0 ? mixColor(pulseCoreBase, rbBase, rbAmt * 0.35) : pulseCoreBase;

      /* ground */
      if (th.bgTop === th.bgBot) {
        ctx.fillStyle = th.bgTop;
      } else {
        const g = ctx.createLinearGradient(0, 0, 0, H);
        g.addColorStop(0, th.bgTop);
        g.addColorStop(1, th.bgBot);
        ctx.fillStyle = g;
      }
      ctx.fillRect(0, 0, W, H);
      if (th.corners) {
        for (let ci = 0; ci < th.corners.length; ci++) {
          const cn = th.corners[ci];
          const gx = cn[0] * W;
          const gy = cn[1] * H;
          const rad = Math.max(W, H) * 0.5;
          const rg = ctx.createRadialGradient(gx, gy, 0, gx, gy, rad);
          rg.addColorStop(0, rgba(cn[2], cn[3]));
          rg.addColorStop(1, rgba(cn[2], 0));
          ctx.fillStyle = rg;
          ctx.fillRect(0, 0, W, H);
        }
      }

      /* cube-face fills sit beneath glints and linework */
      drawCubeFills(t);

      /* glints + cursor-glow face fills */
      let gv;
      for (let i = 0; i < glints.length; i++) {
        const gl = glints[i];
        const pr = (t - gl.t) / gl.dur;
        const a = Math.sin(Math.PI * pr) * (th.dark ? 0.06 : 0.05) * cfg.lineStr;
        if (a <= 0.002) continue;
        facePath(faces[gl.f]);
        ctx.fillStyle = rgba(thGlowFill, a);
        ctx.fill();
      }
      if (cfg.glow && trail.length) {
        const fgain = cfg.glowStr * th.gain;
        for (let i = 0; i < faces.length; i++) {
          const f = faces[i];
          gv = shapeGlow(sampleField((px[f[0]] + px[f[2]]) * 0.5, (py[f[0]] + py[f[2]]) * 0.5), cfg.glowContrast);
          if (gv > 0.12) {
            facePath(f);
            ctx.fillStyle = rgba(thGlowFill, Math.min(0.08, gv * 0.055 * fgain));
            ctx.fill();
          }
        }
      }

      /* base lattice, bucketed by sweep-modulated alpha */
      const baseA = th.lineA * cfg.lineStr;
      const sweepSwing = cfg.sweepBrightness * (0.25 + 0.75 * cfg.lineContrast);
      const maxA = baseA * (cfg.sweep ? 1 + sweepSwing : 1.0001);
      const paths = new Array(BUCKETS);
      for (let i = 0; i < BUCKETS; i++) paths[i] = new Path2D();
      const sweepRad = (cfg.sweepAngle * Math.PI) / 180;
      const sweepDx = Math.cos(sweepRad);
      const sweepDy = Math.sin(sweepRad);
      const sweepFrequency = 0.006 / Math.max(0.1, cfg.sweepWidth);
      const sweepPhase = t * cfg.sweepSpeed;
      for (let i = 0; i < edges.length; i++) {
        const e = edges[i];
        let al = baseA;
        if (cfg.sweep) {
          const sw = 0.5 + 0.5 * Math.sin((e.mrx * sweepDx + e.mry * sweepDy) * sweepFrequency - sweepPhase);
          al = baseA * (1 + sweepSwing * Math.pow(sw, cfg.sweepSoftness));
        }
        let b = ((al / maxA) * (BUCKETS - 1)) | 0;
        if (b >= BUCKETS) b = BUCKETS - 1;
        paths[b].moveTo(px[e.a], py[e.a]);
        paths[b].lineTo(px[e.b], py[e.b]);
      }
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      for (let i = 0; i < BUCKETS; i++) {
        const la = (maxA * (i + 0.5)) / BUCKETS;
        if (la <= 0.004) continue;
        ctx.strokeStyle = rgba(thLine, Math.min(0.9, la));
        ctx.stroke(paths[i]);
      }

      /* luminous passes */
      ctx.globalCompositeOperation = th.blend;

      if (cfg.glow && trail.length) {
        const egain = cfg.glowStr * th.gain;
        for (let i = 0; i < edges.length; i++) {
          const e = edges[i];
          const mx = (px[e.a] + px[e.b]) * 0.5;
          const my = (py[e.a] + py[e.b]) * 0.5;
          gv = shapeGlow(sampleField(mx, my), cfg.glowContrast);
          if (gv < 0.09) continue;
          const gs = gv * egain;
          ctx.beginPath();
          ctx.moveTo(px[e.a], py[e.a]);
          ctx.lineTo(px[e.b], py[e.b]);
          if (gs > 0.16) {
            ctx.strokeStyle = rgba(thGlowLine, Math.min(0.2, gs * 0.16));
            ctx.lineWidth = 4 + gs * 4.5;
            ctx.stroke();
          }
          ctx.strokeStyle = rgba(thGlowLine, Math.min(0.75, gs * 0.62));
          ctx.lineWidth = 1 + gs * 1.3;
          ctx.stroke();
          if (gs > 0.5) {
            ctx.strokeStyle = rgba(thGlowCore, Math.min(0.4, (gs - 0.5) * 0.5));
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      }

      const pSize = cfg.pulseSize;
      const pGlow = cfg.pulseGlow;
      for (let i = 0; i < pulses.length; i++) {
        const p = pulses[i];
        const age = t - p.born;
        let head = Math.min(1, (p.life - age) / 0.7 + 0.0001);
        if (head < 0) head = 0;
        head *= Math.min(1, age / 0.25);
        let pcol = p.col;
        if (rbAmt > 0) {
          const phue = (p.hueSeed + t * 44 * cfg.rainbowSpeed) % 360;
          pcol = mixColor(p.col, hsl2rgb(phue, 0.75, th.dark ? 0.62 : 0.48), rbAmt);
        }
        for (let h2 = 0; h2 < p.hist.length; h2++) {
          const seg = p.hist[h2];
          const ta = Math.exp(-(t - seg.t) / 0.5) * 0.55 * pGlow;
          if (ta < 0.02) continue;
          ctx.beginPath();
          ctx.moveTo(px[seg.a], py[seg.a]);
          ctx.lineTo(px[seg.b], py[seg.b]);
          ctx.strokeStyle = rgba(pcol, Math.min(1, ta));
          ctx.lineWidth = 1.5 * pSize;
          ctx.stroke();
        }
        if (age <= p.life && head > 0) {
          const e2 = edges[p.e];
          const to2 = e2.a === p.from ? e2.b : e2.a;
          const hx = px[p.from] + (px[to2] - px[p.from]) * p.tpos;
          const hy = py[p.from] + (py[to2] - py[p.from]) * p.tpos;
          ctx.beginPath();
          ctx.moveTo(px[p.from], py[p.from]);
          ctx.lineTo(hx, hy);
          ctx.strokeStyle = rgba(pcol, Math.min(1, 0.5 * head * pGlow));
          ctx.lineWidth = 1.5 * pSize;
          ctx.stroke();
          const rg2 = ctx.createRadialGradient(hx, hy, 0, hx, hy, 15 * pSize);
          rg2.addColorStop(0, rgba(pcol, Math.min(1, 0.55 * head * pGlow)));
          rg2.addColorStop(1, rgba(pcol, 0));
          ctx.fillStyle = rg2;
          ctx.beginPath();
          ctx.arc(hx, hy, 15 * pSize, 0, 6.2832);
          ctx.fill();
          ctx.fillStyle = rgba(pcol, Math.min(1, 0.95 * head * pGlow));
          ctx.beginPath();
          ctx.arc(hx, hy, 2.3 * pSize, 0, 6.2832);
          ctx.fill();
          ctx.fillStyle = rgba(thPulseCore, Math.min(1, 0.8 * head * pGlow));
          ctx.beginPath();
          ctx.arc(hx, hy, 1.1 * pSize, 0, 6.2832);
          ctx.fill();
        }
      }

      for (let i = 0; i < blips.length; i++) {
        const bl = blips[i];
        const ba = Math.exp(-(t - bl.t) / 0.22) * 0.7 * pGlow;
        if (ba < 0.03) continue;
        ctx.fillStyle = rgba(thGlowLine, Math.min(1, ba));
        ctx.beginPath();
        ctx.arc(px[bl.v], py[bl.v], 2.4 * pSize, 0, 6.2832);
        ctx.fill();
      }

      /* click-triggered expanding rings, independent of the glow burst */
      if (cfg.clickRing) {
        const ringDuration = Math.max(0.05, cfg.ringDuration);
        const ringRadius = Math.max(0, cfg.ringRadius);
        const ringOpacity = clamp01(cfg.ringOpacity);
        const ringWidth = Math.max(0.1, cfg.ringWidth);
        for (let i = 0; i < bursts.length; i++) {
          const bu = bursts[i];
          const bage = t - bu.t;
          if (bage > ringDuration) continue;
          const ringProgress = Math.max(0, bage / ringDuration);
          const rr = 8 + ringProgress * ringRadius;
          const ra = (1 - ringProgress) * ringOpacity;
          ctx.beginPath();
          ctx.arc(bu.x, bu.y, rr, 0, 6.2832);
          ctx.strokeStyle = rgba(thGlowLine, ra);
          ctx.lineWidth = ringWidth;
          ctx.stroke();
        }
      }

      ctx.globalCompositeOperation = 'source-over';
    }

    /* ---------------------------------------------------------------- loop */

    let tNow = 0;
    let tLast = performance.now() / 1000;
    let raf = 0;
    let offscreen = false;

    /* one frame: advance the clock, the field, the geometry and the pulses,
       then paint. Exposed so a test can step it by hand. */
    function tick(nowMs) {
      const now = nowMs / 1000;
      let dt = now - tLast;
      tLast = now;
      cfg = frameSettings();
      if (geometryScale !== cfg.scale) buildGeometry();
      if (dt > 0.05) dt = 0.05;
      if (dt < 0) dt = 0;
      tNow += dt;

      if (demoDrive) {
        const gp = ghost(tNow);
        cursor.x = gp.x;
        cursor.y = gp.y;
        if (cfg.glow) pushTrail(gp.x, gp.y, tNow);
        if (cfg.pulses && Math.random() < dt * 0.1) bursts.push({ x: gp.x, y: gp.y, t: tNow, big: false });
      }

      rebuildField(tNow);
      updateVerts(tNow, dt || 0.0001);
      updatePulses(dt);
      render(tNow);
    }

    function loop(nowMs) {
      raf = 0;
      tick(nowMs);
      schedule();
    }

    /* a frame is only ever asked for when there is something to see it */
    function schedule() {
      if (raf || still || opts.paused || offscreen || document.hidden) return;
      raf = requestAnimationFrame(loop);
    }

    function halt() {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    function resume() {
      tLast = performance.now() / 1000;
      schedule();
    }

    /* the still frame: reduced motion, data-motion="static", or a test */
    function renderOneFrame() {
      cfg = frameSettings();
      if (geometryScale !== cfg.scale) buildGeometry();
      updateVerts(0, 0.016);
      render(0);
    }

    function setProgress(value) {
      mix = clamp01(Number(value) || 0);
      applyPalette();
      if (still) renderOneFrame();
    }

    /* the export's updateScrollTransition: window scroll from the page top,
       start → end viewport heights */
    function fromWindowScroll() {
      const vh = Math.max(1, innerHeight || 1);
      const start = opts.start * vh;
      const end = Math.max(start + 1, opts.end * vh);
      setProgress((scrollY - start) / (end - start));
    }

    let autoBind = false;

    function useWindowScroll(enabled) {
      enabled = !!enabled;
      if (enabled === autoBind) {
        if (enabled) fromWindowScroll();
        return;
      }
      autoBind = enabled;
      if (autoBind) {
        addEventListener('scroll', fromWindowScroll, { passive: true });
        fromWindowScroll();
      } else {
        removeEventListener('scroll', fromWindowScroll);
      }
    }

    function resize() {
      sizeCanvas();
      sizeGrid();
      buildGeometry();
      if (autoBind) fromWindowScroll();
      else if (still) renderOneFrame();
    }

    /* ------------------------------------------------------------- wiring */

    const onVisibility = () => (document.hidden ? halt() : resume());
    const io = new IntersectionObserver(
      (entries) => {
        offscreen = !entries[entries.length - 1].isIntersecting;
        if (offscreen) halt();
        else resume();
      },
      { threshold: 0 }
    );
    /* Debounced, and skipped when the box has not actually changed — the
       observer reports once on observe, and a mobile browser bar collapsing
       reports too, and neither should rebuild the lattice and clear the
       canvas for nothing. */
    let resizeTimer = 0;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const bounds = el.getBoundingClientRect();
        if (Math.abs(bounds.width - W) < 1 && Math.abs(bounds.height - H) < 1) return;
        resize();
      }, 120);
    };
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(onResize) : null;

    resize();
    io.observe(el);
    if (ro) ro.observe(el);
    else addEventListener('resize', onResize, { passive: true });
    if (!still) {
      document.addEventListener('visibilitychange', onVisibility);
      addEventListener('pointermove', onPointerMove, { passive: true });
      addEventListener('pointerdown', onPointerDown);
      document.addEventListener('pointerleave', onPointerLeave);
      addEventListener('blur', onPointerLeave);
    }
    /* A ground that is simply light, or simply teal, rather than one on its
       way between them: the section that only wants the texture holds the
       blend at one end and never moves it. Everything else about the engine
       is unchanged — it still animates, still reads the pointer, still backs
       off on a slow device. */
    if (opts.hold !== null) setProgress(opts.hold);
    else if (opts.driven) setProgress(0);
    else useWindowScroll(true);
    if (!still && !opts.paused) schedule();

    function destroy() {
      halt();
      clearTimeout(resizeTimer);
      io.disconnect();
      if (ro) ro.disconnect();
      else removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      removeEventListener('pointermove', onPointerMove);
      removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointerleave', onPointerLeave);
      removeEventListener('blur', onPointerLeave);
      removeEventListener('scroll', fromWindowScroll);
      canvas.remove();
    }

    return {
      setProgress,
      useWindowScroll,
      colorsAt,
      tick,
      resize,
      destroy,
      get progress() {
        return mix;
      },
      get autoBind() {
        return autoBind;
      },
      /* the export's scrollConfig, live */
      get scrollConfig() {
        return {
          enabled: true,
          autoBind,
          mix,
          start: opts.start,
          end: opts.end,
          easing: EASING,
          direction: 'light-to-dark',
          ...TEXT,
          light: { label: LABELS.light, settings: { ...LIGHT } },
          dark: { label: LABELS.dark, settings: { ...DARK } },
        };
      },
    };
  }

  /* ------------------------------------------------------------- component */

  class FeHoneycomb extends HTMLElement {
    connectedCallback() {
      if (this.engine) return;
      this.setAttribute('aria-hidden', 'true');

      const canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      /* the export's canvas id, for anything written against its README */
      if (!document.getElementById('fe-honeycomb-canvas')) canvas.id = 'fe-honeycomb-canvas';
      this.append(canvas);

      const host = this.closest('[data-fe-honeycomb-host]') || this.parentElement || this;
      const motion = this.dataset.motion || 'auto';
      const start = parseFloat(this.dataset.start);
      const end = parseFloat(this.dataset.end);
      const hold = parseFloat(this.dataset.hold);

      this.engine = createEngine(this, canvas, host, {
        still: REDUCED.matches || motion === 'static',
        lite: motion === 'lite',
        palette: parsePalette(this.dataset.palette),
        paused: this.hasAttribute('data-paused'),
        driven: this.hasAttribute('data-driven'),
        hold: Number.isFinite(hold) ? clamp01(hold) : null,
        start: Number.isFinite(start) ? start : RANGE.start,
        end: Number.isFinite(end) ? end : RANGE.end,
      });

      /* the export's window-level handle: the README's way for a site
         controller (GSAP, ScrollTrigger…) to take the progress over */
      if (!window.FE_HONEYCOMB || !document.contains(window.FE_HONEYCOMB.canvas)) {
        const el = this;
        window.FE_HONEYCOMB = {
          canvas,
          config: Object.freeze({ ...LIGHT }),
          get scrollConfig() {
            return el.engine ? el.engine.scrollConfig : null;
          },
          setScrollProgress(value) {
            el.useWindowScroll(false);
            el.setProgress(value);
          },
          useWindowScroll(enabled) {
            el.useWindowScroll(enabled);
          },
          redraw() {
            el.redraw();
          },
        };
      }

      /* a change of motion preference is a different engine, not a setting */
      this.onMotionPref = () => {
        const progress = this.progress;
        this.teardown();
        this.connectedCallback();
        this.setProgress(progress);
      };
      REDUCED.addEventListener('change', this.onMotionPref);
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      REDUCED.removeEventListener('change', this.onMotionPref);
      this.engine?.destroy();
      this.engine = null;
    }

    /* 0 = fully the light look, 1 = fully the teal one */
    setProgress(value) {
      this.engine?.setProgress(value);
    }

    /* true = read the window scroll (the export's default); false = wait to be
       fed through setProgress */
    useWindowScroll(enabled) {
      this.engine?.useWindowScroll(enabled);
    }

    get progress() {
      return this.engine ? this.engine.progress : 0;
    }

    /* the ground and role colours the palette would resolve to at a progress */
    colorsAt(value) {
      return this.engine?.colorsAt(value);
    }

    /* step one animated frame by hand (tests; data-paused keeps the loop off) */
    tick(nowMs) {
      this.engine?.tick(nowMs ?? performance.now());
    }

    redraw() {
      this.engine?.resize();
    }
  }

  customElements.define('fe-honeycomb', FeHoneycomb);
})();
